import { TIME } from '../../constants/time';

/**
 * Formats a duration in seconds into a standard HH:mm:ss or mm:ss string representation.
 * Handles invalid, negative, or missing values by returning a fallback `--:--` string.
 *
 * @param totalSeconds - The duration in seconds to format.
 * @returns A formatted string like "1:30:45" (if hours > 0) or "45:30" (if < 1 hour).
 *          Returns `--:--` for invalid, negative, or undefined inputs.
 */
export const formatSeconds = (totalSeconds?: number): string => {
  if (!totalSeconds || Number.isNaN(totalSeconds) || totalSeconds < 0) {
    return '--:--';
  }

  const hours = Math.floor(totalSeconds / TIME.SECONDS.PER_HOUR);
  const minutes = Math.floor((totalSeconds % TIME.SECONDS.PER_HOUR) / TIME.SECONDS.PER_MINUTE);
  const seconds = Math.floor(totalSeconds % TIME.SECONDS.PER_MINUTE);

  const formattedSeconds = padWithLeadingZero(seconds);
  const formattedMinutes = padWithLeadingZero(minutes);

  if (hours > 0) {
    return `${hours}:${formattedMinutes}:${formattedSeconds}`;
  }
  return `${formattedMinutes}:${formattedSeconds}`;
};

/**
 * Pads a single-digit string or number with a leading zero so it has a length of at least 2.
 * Used primarily for formatting time components (hours, minutes, seconds).
 *
 * @param value - The number or string to pad.
 * @returns The padded string (e.g., `5` becomes `05`, `12` remains `12`).
 */
export const padWithLeadingZero = (value: number | string): string => {
  return String(value).padStart(2, '0');
};

/**
 * Creates a new Date instance representing the very start (00:00:00.000)
 * of the same local day as the provided date.
 *
 * @param date - The reference date.
 * @returns A new Date object set to midnight local time.
 */
export const moveToStartOfDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

/**
 * Creates a new Date instance representing the very end (23:59:59.999)
 * of the same local day as the provided date.
 *
 * @param date - The reference date.
 * @returns A new Date object set to one millisecond before the next midnight local time.
 */
export const moveToEndOfDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
};

/**
 * Aligns a starting time to the next interval matching the given duration,
 * but **only if the duration is a multiple of 5 minutes**.
 *
 * If the duration is not a multiple of 5, the original time is returned unaligned.
 * If the time is already aligned (e.g., 10:30 with duration 30), it returns the original time.
 * If the time is not aligned (e.g., 10:15 with duration 30), it aligns to the next block (10:30).
 *
 * @param startTime - The date/time to align.
 * @param duration - The duration block in minutes to align to (must be a multiple of 5 for alignment).
 * @returns The aligned timestamp in milliseconds since the epoch.
 */
export const alignStartTime = (startTime: Date, duration: number): number => {
  if (duration % 5 !== 0) {
    return startTime.getTime(); // No alignment for durations not divisible by 5
  }

  const minutes = startTime.getMinutes();
  const remainder = minutes % duration;

  if (remainder === 0) {
    return startTime.getTime(); // Already aligned
  }
  return startTime.getTime() + (((duration - remainder) * 60000) % (duration * 60000));
};

/**
 * Converts total minutes since midnight to HH:mm format
 * @param totalMinutes - minutes since midnight (0-1439)
 * @returns time string in HH:mm format (e.g., "09:30", "14:15")
 */
export const minutesToTimeString = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${padWithLeadingZero(hours)}:${padWithLeadingZero(minutes)}`;
};

/**
 * Converts HH:mm time string to total minutes since midnight
 * @param timeString - time in HH:mm format
 * @returns total minutes since midnight
 */
export const timeStringToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

/** End of day time string (23:59) used for availability schedules */
export const END_OF_DAY_TIME = '23:59';

/**
 * Converts HH:mm time string to minutes, treating END_OF_DAY_TIME as full day (1440 minutes)
 * Use this when converting availability intervals where 23:59 represents end of day
 * @param timeString - time in HH:mm format
 * @returns total minutes since midnight, or TIME.MINUTES.PER_DAY for end of day
 */
export const timeStringToMinutesEndOfDay = (timeString: string): number => {
  if (timeString === END_OF_DAY_TIME) {
    return TIME.MINUTES.PER_DAY;
  }
  return timeStringToMinutes(timeString);
};

/**
 * Converts minutes to HH:mm time string, treating full day (1440 minutes) as END_OF_DAY_TIME
 * Use this when converting availability intervals where end of day should display as 23:59
 * @param totalMinutes - minutes since midnight
 * @returns time string in HH:mm format, or "23:59" for full day
 */
export const minutesToTimeStringEndOfDay = (totalMinutes: number): string => {
  if (totalMinutes === TIME.MINUTES.PER_DAY) {
    return END_OF_DAY_TIME;
  }
  return minutesToTimeString(totalMinutes);
};
